import React from 'react';
import { get } from 'lodash';
import { reaction } from 'mobx';
import { inject, observer } from 'mobx-react';

import { toDecimal } from 'core/util';
import { Flex } from 'core/components';
import { Field, Form, Select } from 'core/form';
import { buildFilterGroup } from 'core/util/filters';
import { ENTITY_TYPES } from 'shared/hybrid/constants';
import { AWS } from 'app/views/hybrid/maps/cloudDimensionConstants';
import TrafficChart from 'app/views/hybrid/maps/components/trafficCharts/TrafficChart';
import LightweightDataViewWrapper from 'app/components/dataviews/LightweightDataViewWrapper';

const { FIREWALL, SUBNET } = ENTITY_TYPES.get('aws');

const fields = {
  action: {
    defaultValue: 'all',
    options: [
      { label: 'All Actions', value: 'all' },
      { label: 'ACCEPT', value: 'ACCEPT' },
      { label: 'ALERT', value: 'ALERT' },
      { label: 'ALERTED', value: 'ALERTED' },
      { label: 'ALLOW', value: 'ALLOW' },
      { label: 'ALLOWED', value: 'ALLOWED' },
      { label: 'DENIED', value: 'DENIED' },
      { label: 'DENY', value: 'DENY' },
      { label: 'DROP', value: 'DROP' },
      { label: 'DROP-ALL-PACKETS', value: 'DROP-ALL-PACKETS' },
      { label: 'DROPPED', value: 'DROPPED' },
      { label: 'PASS', value: 'PASS' },
      { label: 'REJECT', value: 'REJECT' },
      { label: 'REJECTED', value: 'REJECTED' },
      { label: 'RESET-BOTH', value: 'RESET-BOTH' },
      { label: 'RESET-CLIENT', value: 'ESET-CLIENT' },
      { label: 'RESET-SERVER', value: 'RESET-SERVER' }
    ]
  },

  subnets: {
    defaultValue: 'all',
    options: [{ label: 'All', value: 'all' }]
  },

  aggregate: {
    defaultValue: 'avg_flows_per_sec',
    options: [
      { label: 'Avg flows/s', value: 'avg_flows_per_sec' },
      { label: 'Flows/s 95th Percentile', value: 'p95th_flows_per_sec' },
      { label: 'Flows/s Max', value: 'max_flows_per_sec' },
      { label: 'Bits/s Sampled at Ingress + Egress 95th Percentile', value: 'p95th_bits_per_sec' },
      { label: 'Packets/s Sampled at Ingress + Egress 95th Percentile', value: 'p95th_pkts_per_sec' }
    ]
  }
};

const options = {
  name: 'Firewall Rule Group Traffic Charts'
};

@Form({ fields, options })
@inject('$colors', '$hybridMap')
@observer
export default class AwsFirewallRuleGroupTrafficCharts extends React.Component {
  state = {};

  formDisposer;

  componentDidMount() {
    const { form, onFormChange } = this.props;

    this.formDisposer = reaction(
      () => form.getValues(),
      (values) => {
        if (onFormChange) {
          onFormChange(values);
        }
      }
    );
  }

  componentWillUnmount() {
    if (this.formDisposer) {
      this.formDisposer();
    }
  }

  get selectedAggregate() {
    const { form } = this.props;
    return form.getValue('aggregate');
  }

  get baseQuery() {
    return {
      aggregateTypes: [this.selectedAggregate],
      all_devices: false,
      device_types: [AWS.DATASET],
      metric: ['Traffic'],
      show_overlay: true,
      show_total_overlay: true
    };
  }

  getLastDataPoint = (results) => {
    const totalRow = results.models.find((model) => model.get('key') === 'Total');

    if (totalRow) {
      return toDecimal(totalRow.get(this.selectedAggregate));
    }

    return '';
  };

  hasData = (dv) =>
    dv && dv.queryBuckets && dv.queryBuckets.activeBuckets.every((bucket) => bucket.queryResults.size > 0);

  get selectedFirewallSubnet() {
    const { form } = this.props;
    return form.getValue('subnets');
  }

  get selectedAction() {
    const { form } = this.props;
    return form.getValue('action');
  }

  get actionFilterGroup() {
    const { selectedAction } = this;

    if (selectedAction === 'all') {
      return null;
    }

    return buildFilterGroup({
      name: 'firewall-action-filter',
      filters: [
        {
          filterField: 'kt_aws_action',
          operator: '=',
          filterValue: selectedAction
        }
      ]
    });
  }

  get filters() {
    const { actionFilterGroup, selectedFirewallSubnet, awsFirewall } = this;

    let subnetFilterGroup = [];
    const filterGroups = [];

    const firewallFilterGroup = buildFilterGroup({
      name: 'firewall-filter',
      connector: 'Any',
      filters: [
        {
          filterField: AWS.NON_DIRECTIONAL[FIREWALL],
          operator: '=',
          filterValue: awsFirewall.FirewallArn
        }
      ]
    });

    filterGroups.push(firewallFilterGroup);

    if (selectedFirewallSubnet !== 'all') {
      subnetFilterGroup = buildFilterGroup({
        name: '',
        filters: [
          {
            filterField: AWS.NON_DIRECTIONAL.FIREWALL_OBSERVING_SUBNET_ID,
            operator: '=',
            filterValue: selectedFirewallSubnet
          }
        ]
      });

      filterGroups.push(subnetFilterGroup);
    }

    if (actionFilterGroup !== null) {
      filterGroups.push(actionFilterGroup);
    }

    return {
      filterGroups,
      connector: 'All'
    };
  }

  get query() {
    const dimension = [
      'IP_src',
      'kt_aws_src_subnet_id',
      'IP_dst',
      'kt_aws_dst_subnet_id',
      'AS_dst',
      'kt_aws_action',
      AWS.NON_DIRECTIONAL.FIREWALL_OBSERVING_SUBNET_ID
    ];
    return {
      ...this.baseQuery,
      filters: this.filters,
      dimension,
      query_title: 'Aws Firewall Traffic Charts'
    };
  }

  get topology() {
    const { $hybridMap } = this.props;
    return $hybridMap.awsCloudMapCollection.topology;
  }

  get awsFirewall() {
    const { firewallId } = this.props;
    return get(this.topology.Entities, `${[FIREWALL]}.${firewallId}`);
  }

  get firewallSubnetIds() {
    const { Firewall } = this.awsFirewall;

    return (
      Firewall?.SubnetMappings?.map((mapping) => {
        const { SubnetId } = mapping;

        return SubnetId;
      }) ?? []
    );
  }

  get subnetOptions() {
    const subnetOptions = this.firewallSubnetIds
      .map((subnetId) => {
        const subnetEntity = get(this.topology.Entities, `${[SUBNET]}.${subnetId}`);

        if (!subnetEntity) {
          return null;
        }

        return {
          label: subnetEntity.Name,
          value: subnetEntity.id
        };
      })
      .filter((option) => option);

    return [{ label: 'All', value: 'all' }, ...subnetOptions];
  }

  render() {
    return (
      <Flex flexDirection="column" flex={1} minHeight={0}>
        <Flex gap={1} width="100%">
          <Field flex={1} name="action">
            <Select fill preventEnterSelection />
          </Field>

          <Field flex={1} name="subnets" options={this.subnetOptions}>
            <Select fill showFilter />
          </Field>

          <Field flex={1} name="aggregate">
            <Select fill />
          </Field>
        </Flex>
        <LightweightDataViewWrapper query={this.query}>
          {({ dataview, loading, results }) => {
            const hasData = this.hasData(dataview);

            return (
              <TrafficChart
                type="outbound"
                dataview={dataview}
                loading={loading}
                showExplorerButton
                units="Average flows/s"
                lastDataPoint={this.getLastDataPoint(results)}
                noResultsConfig={{
                  hasData: true, // we don't ever want to show the inbound/outbound empty state for single charts
                  outbound: { hasData }
                }}
                query={this.query}
              />
            );
          }}
        </LightweightDataViewWrapper>
      </Flex>
    );
  }
}
